import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { resetTenantLocalData } from '@/db/reset';
import { useImpersonationStore } from '@/stores/impersonationStore';

interface User {
  id: string;
  email: string;
  name: string;
  tenant_id: string | null;
  /**
   * Filter-locked branch. ONLY set when the user is restricted to a single
   * branch (cashier, kitchen, branch admin, etc.). NULL for owners and
   * super admins — list pages that read this MUST NOT auto-filter when
   * it's null.
   */
  branch_id?: string | null;
  /**
   * Preferred default branch for POS / KDS bootstrap. Always present when
   * the user has any branch assignment, even for owners. List pages
   * should NOT use this as a filter — only POS / KDS read it.
   */
  primary_branch_id?: string | null;
  /** All branches the user is assigned to via user_branches. */
  branch_ids?: string[];
  /**
   * Mirror of branch_id != null. True when the user can ONLY see their
   * branch's data; false for owners and super admins.
   */
  branch_locked?: boolean;
  roles: string[];
  /**
   * Per-user route overrides on top of the role defaults. NULL/undefined
   * means 'inherit fully'. Entries here win over the role's default for
   * that exact path. See lib/permissions.canAccess.
   */
  permissions?: Record<string, boolean> | null;
  /**
   * This tenant's super-admin-set default access per role, from the
   * /super/access matrix. Sits between the global ACCESS map and
   * `permissions` above in priority. See lib/permissions.canAccess.
   */
  role_overrides?: Record<string, Record<string, boolean>> | null;
}

interface Tenant {
  id: string;
  slug: string;
  name: string;
}

// Shape returned by POST /api/v1/auth/login (response.data.data).
export interface LoginPayload {
  access_token: string;
  user: User;
  tenant: Tenant | null;
  landing_path: string;
}

interface AuthState {
  accessToken: string | null;
  user: User | null;
  tenant: Tenant | null;
  /** Accepts the snake_case payload from the API directly. */
  set: (p: LoginPayload) => void;
  clear: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      user: null,
      tenant: null,
      // On login: if the tenant has changed since last session, wipe
      // tenant-scoped local data so the previous tenant's menu / orders /
      // pending sync ops don't leak into the new one's POS.
      set: (p) => {
        const prev = get().tenant?.id;
        if (prev && prev !== p.tenant?.id) {
          resetTenantLocalData().catch(() => { /* nothing to do — best effort */ });
        }
        set({ accessToken: p.access_token, user: p.user, tenant: p.tenant });
      },
      // On logout: always wipe. The next user might be a different tenant.
      // Also drop any platform-admin tenant impersonation — the next person
      // to sign in on this browser shouldn't inherit someone else's context.
      clear: () => {
        resetTenantLocalData().catch(() => {});
        useImpersonationStore.getState().exit();
        set({ accessToken: null, user: null, tenant: null });
      },
    }),
    {
      name: 'ofi-cia-auth',
      storage: createJSONStorage(() => localStorage),
    },
  ),
);
